/**
 * useStaticSeo — SEO metadata for a static page (ADR 0142).
 *
 * Combines the generated static-SEO registry (`static-seo.gen.ts`, the canonical
 * path + og:type per indexable static route, SSOT'd in routes.yaml) with the
 * page's own localized copy (kept in i18n) to produce a complete `SeoMeta`:
 *   - title / description : passed in by the page (from `t(...)`)
 *   - canonical / hreflang: derived here from the route path + current locale
 *
 * Canonical is computed client-safely via the hydrated `RuntimeConfig.siteUrl`
 * (`useConfig`) rather than the server-only `getSiteUrl()`, so loader-less
 * static pages get canonical + alternates with no per-page server loader.
 *
 * Lives in the `hooks` layer (not `lib`) because it consumes `useConfig`
 * (providers) and `useLocale` (i18n) — keeping it out of `lib` is what avoids
 * the `lib → providers → lib` dependency cycle. The pure proto mapper and the
 * generated registry stay in `lib/seo` and are imported downward.
 */
import { BUSINESS_PROFILE } from '@letsgethome/lib/business';
import { pageUrlsWith } from '@letsgethome/lib/weburl';
import type { LocalizedText } from '@letsgethome/types/branded';

import { useLocale } from '../../i18n';
import { routeRobots } from '../../lib/seo/robots';
import { ogLocale } from '../../lib/seo/seo-from-proto';
import { staticSeo, type StaticSeoRoutePath } from '../../lib/seo/static-seo.gen';
import { useConfig } from '../../providers/ConfigProvider';
import type { SeoMeta } from '../../types/seo';

export interface StaticSeoCopy {
  /** Localized `<title>` (from the page's i18n — mint via `t('key')`). */
  readonly title: LocalizedText;
  /**
   * Localized meta description (from the page's i18n). REQUIRED: every route
   * in the static-SEO registry is indexable, so shipping one without a meta
   * description is an SEO defect (Ahrefs "missing description") — omission is
   * a compile error, not an audit finding. Mint via `t('seo.description')`;
   * the `…seo.description` key convention is what opts the copy into the
   * ui-strings 110–160 length gate (enforced by `seo-description-key`).
   */
  readonly description: LocalizedText;
}

/**
 * Build the `SeoMeta` for a static route, deriving canonical + hreflang from the
 * route's canonical path (the registry key — the public-URL SSOT) and the
 * current locale + hydrated site origin. `path` is a `StaticSeoRoutePath`, so a
 * page that passes a path not in the root sitemap's static-SEO set is a compile
 * error (ADR 0142 routes-SSOT consolidation).
 */
export function useStaticSeo(path: StaticSeoRoutePath, copy: StaticSeoCopy): SeoMeta {
  const { siteUrl } = useConfig();
  const locale = useLocale();
  const { ogType } = staticSeo[path];
  // ONE composition of ONE path (V28, the web twin of #4711). This used to be
  // two calls — `buildCanonicalWith(siteUrl, locale, path)` and
  // `buildAlternatesWith(siteUrl, path)` — which took `path` twice and required
  // nothing of the relationship between their results, so a canonical that was
  // not a member of its own hreflang set was representable. `canonical` is now
  // literally `alternates[locale]`.
  const { canonical, alternates } = pageUrlsWith(siteUrl, locale, path);
  return {
    title: copy.title,
    description: copy.description,
    canonical,
    alternates,
    // Every static route gets the brand share image. A static page has no
    // photo of its own, so before this each one emitted a complete OG set
    // MINUS og:image while still declaring `twitter:card=summary_large_image`
    // — a card that cannot render (Ahrefs flagged every `/tools/*` page and
    // `/forms`, including the site's top organic page, 2026-07-20).
    //
    // Supplying it HERE rather than per page is what closes the class: this
    // hook is the only way a static route builds its SeoMeta, so a new static
    // page cannot ship without a social image, exactly as it cannot ship
    // without `description`.
    //
    // The source is the `manifests/business.yaml` brand share image — the SAME
    // one the backend resolves for district hubs and the RealEstateAgent card
    // (`seo::brand_share_image`), so the two can never drift. Swapping in a
    // purpose-built card is a one-line change to that manifest, with no code
    // edit here — dimensions included, since they come from the same manifest
    // (see `ogImageWidth`/`ogImageHeight` below). The earlier note here claimed
    // dimensions were "deliberately NOT emitted" to avoid them going stale on
    // that swap; it was contradicted three lines later by the code, and the
    // staleness it feared cannot happen while the values are read from
    // BUSINESS_PROFILE rather than restated (V28 doc rot).
    ogImage: `${siteUrl.replace(/\/$/, '')}${BUSINESS_PROFILE.imagePath}`,
    ogImageAlt: copy.title,
    // The share image's real size (SEO F7). Carrying it does two things:
    // Facebook stops having to re-fetch the image to learn its dimensions,
    // and `SeoHead` derives the `twitter:card` type from it rather than
    // asserting `summary_large_image` next to an asset too small for one.
    ogImageWidth: BUSINESS_PROFILE.imageWidth,
    ogImageHeight: BUSINESS_PROFILE.imageHeight,
    ogType,
    locale: ogLocale(locale),
    // Robots, DERIVED from the same manifest row as the canonical above
    // (V8-proper). Every static page reaches `SeoHead` through this hook, so
    // supplying it here is what makes `robots` explicit at 18 call sites
    // without any of them typing an indexability claim: the page states its
    // PATH, and `manifests/routes.yaml` states whether that path is indexable.
    // Flip `indexable:` in the manifest and the page follows on the next sync —
    // the page and the sitemap cannot drift because there is only one fact.
    robots: routeRobots(path),
  };
}
